import { observer } from "mobx-react"
import { useStore } from "@features/common"
import { ButtonContained } from "@ui"
import { IconRefresh } from "@lib/icons"

import { PostGridElement } from "./posts-grid-element"

import type { ReactElement, ComponentPropsWithoutRef } from "react"

type PostsGridProps = {
  dataModel?: "BlogModel" | "SaferDatingModel"
  postUrlPrefix?: string
  renderButton?: (onClick: () => Promise<void>) => ReactElement
  hasRotation?: boolean
} & ComponentPropsWithoutRef<"section">

export const PostsGrid = observer(function PostsGrid({
  dataModel = "BlogModel",
  postUrlPrefix,
  renderButton,
  hasRotation,
  ...otherProps
}: PostsGridProps) {
  const store = useStore()
  const {
    pagination,
    existsGridPosts,
    postsChunks,
    getPosts,
    getTemplate,
  } = store[dataModel]

  if (!existsGridPosts) return null

  const handleClickLoadPostsButton = async () => {
    if (pagination.page !== pagination.pageCount) {
      await getPosts({
        page: pagination.page + 1,
      })
    }
  }

  const numberOfPostsRemaining =
    pagination.rowCount - pagination.page * pagination.pageSize

  const numberOfPostsToLoad =
    numberOfPostsRemaining > pagination.pageSize
      ? pagination.pageSize
      : numberOfPostsRemaining

  const isVisibleLoadAction = pagination.page !== pagination.pageCount

  const resolvedRenderButton = () => {
    if (renderButton) {
      return renderButton(handleClickLoadPostsButton)
    } else {
      return (
        <ButtonContained
          onClick={handleClickLoadPostsButton}
          className="w-full max-w-[22.5rem] space-x-2.5"
          data-test-id="articles_grid__load_button"
        >
          <span>
            {getTemplate("show_more")} {numberOfPostsToLoad}
          </span>
          <IconRefresh className="text-white" />
        </ButtonContained>
      )
    }
  }

  return (
    <section
      className={"container tablet-s:px-6"}
      data-test-id="articles_grid"
      {...otherProps}
    >
      <div className="space-y-3 tablet:space-y-4">
        {postsChunks.map((chunk) => (
          <PostGridElement
            key={chunk[0].id}
            posts={chunk}
            postUrlPrefix={postUrlPrefix}
            hasRotation={hasRotation}
          />
        ))}
      </div>
      {isVisibleLoadAction && (
        <div className="text-center mt-6.5 tablet:mt-13">
          {resolvedRenderButton()}
        </div>
      )}
    </section>
  )
})
